import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from '../firebase';

const Login = ({ isSidebar, isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const [tab, setTab] = useState('email'); // 'email' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);

  const { login, loginWithPhone, user } = useAuth();
  const navigate = useNavigate();

  // Helper to initialize invisible reCAPTCHA
  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {},
        'expired-callback': () => {
          window.recaptchaVerifier = null;
        }
      });
    }
    return window.recaptchaVerifier;
  };

  // Send OTP using Firebase Phone Auth
  const handleSendOtp = async () => {
    const cleanDigits = phone.replace(/\D/g, '');
    if (!cleanDigits || cleanDigits.length < 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    try {
      setOtpLoading(true);
      const appVerifier = setupRecaptcha();
      const formattedPhone = phone.trim().startsWith('+') ? phone.trim() : `+91${cleanDigits.slice(-10)}`;

      const confirmation = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      setConfirmationResult(confirmation);
      setOtpSent(true);
      toast.success(`OTP sent to ${formattedPhone}`);
    } catch (error) {
      console.error('Firebase SMS OTP error:', error);
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {}
        window.recaptchaVerifier = null;
      }
      if (error.code === 'auth/invalid-phone-number') {
        toast.error('Invalid phone number format.');
      } else if (error.code === 'auth/quota-exceeded') {
        toast.error('SMS quota exceeded. Please try again later or use test phone number.');
      } else if (error.code === 'auth/too-many-requests') {
        toast.error('Too many requests. Please try again later.');
      } else {
        toast.error(error.message || 'Failed to send OTP. Please check your Firebase settings.');
      }
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP and complete login
  const handleVerifyOtp = async () => {
    if (!otp || otp.trim().length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    if (!confirmationResult) {
      toast.error('Please request an OTP first');
      return;
    }

    try {
      setOtpLoading(true);
      const result = await confirmationResult.confirm(otp.trim());
      const firebaseUser = result.user;

      const resp = await loginWithPhone(firebaseUser.phoneNumber || phone);
      if (resp.success) {
        toast.success('Login successful!');
        if (onClose) onClose();
        navigate(resp.data?.role === 'admin' ? '/admin' : '/store');
      } else {
        toast.error(resp.message || 'Login failed');
      }
    } catch (error) {
      console.error('Firebase OTP verification error:', error);
      if (error.code === 'auth/invalid-verification-code') {
        toast.error('Incorrect OTP. Please check and try again.');
      } else if (error.code === 'auth/code-expired') {
        toast.error('OTP has expired. Please request a new OTP.');
      } else {
        toast.error(error.message || 'OTP verification failed');
      }
    } finally {
      setOtpLoading(false);
    }
  };

  // If used as a modal and it's explicitly false, don't render anything
  if (isOpen === false) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login(email, password);
      toast.success('Login successful!');
      if (onClose) onClose(); 
      navigate(response.data?.role === 'admin' ? '/admin' : '/store');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Content for Logged In users
  if (user) {
    const loggedInContent = (
      <div className="w-full max-w-sm border border-gray-100 bg-white rounded-xl shadow-xs p-5 text-center transition-all duration-200 relative">
        {isOpen && (
          <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 font-semibold text-sm">✕</button>
        )}
        <h2 className="text-sm font-semibold text-gray-800">Welcome, {user.name || user.email}!</h2>
        <p className="text-xs text-gray-400 mt-1">You are already logged in.</p>
      </div>
    );

    if (isOpen) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 antialiased">
          {loggedInContent}
        </div>
      );
    }

    return (
      <div className={isSidebar ? 'p-3' : 'min-h-screen bg-slate-50/50 flex items-center justify-center p-4 antialiased'}>
        {loggedInContent}
      </div>
    );
  }

  // Pure login card markup container
  const formContent = (
    <div
      className={
        isSidebar
          ? 'bg-white rounded-xl border border-gray-100 p-5 max-w-sm w-full shadow-xs relative'
          : 'bg-white/95 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-6 w-full max-w-sm animate-fade-slide relative'
      }
    >
      {/* Invisible reCAPTCHA container for Firebase Phone Auth */}
      <div id="recaptcha-container"></div>

      {/* Pop-up Close (X) Button */}
      {isOpen && (
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-xs font-bold z-50"
          aria-label="Close Modal"
        >
          ✕
        </button>
      )}

      {/* Header */}
      <div className="text-center mb-5">
        <div className="mx-auto mb-2 h-11 w-11 rounded-full bg-white flex items-center justify-center border border-gray-100 shadow-xs overflow-hidden">
          <img 
            src="/images/pronest_logo.png" 
            alt="ProNest Logo" 
            className="h-6 w-6 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
        <h1 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
          ProNest
        </h1>
        <p className="text-[11px] text-gray-400 font-medium tracking-wide mt-0.5">Welcome Back</p>
      </div>

      {/* Login tabs */}
      <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl p-1 mb-3">
        <button
          type="button"
          onClick={() => setTab('email')}
          className={`flex-1 text-xs font-semibold py-1.5 rounded-lg transition-colors ${
            tab === 'email' ? 'bg-white text-gray-900 shadow-xs border border-gray-100' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Email
        </button>
        <button
          type="button"
          onClick={() => setTab('otp')}
          className={`flex-1 text-xs font-semibold py-1.5 rounded-lg transition-colors ${
            tab === 'otp' ? 'bg-white text-gray-900 shadow-xs border border-gray-100' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          OTP (Mobile)
        </button>
      </div>

      {/* Login Form */}
      {tab === 'email' ? (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white/50 transition-colors"
              placeholder="name@company.com"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white/50 transition-colors"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg font-medium text-xs shadow-xs transition-colors disabled:opacity-40 mt-1"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Mobile Number
            </label>
            <input
              type="tel"
              inputMode="numeric"
              value={phone}
              disabled={otpSent || otpLoading}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white/50 transition-colors disabled:bg-gray-100"
              placeholder="10-digit mobile (e.g. 9876543210)"
              required
            />
          </div>

          {!otpSent ? (
            <button
              type="button"
              disabled={otpLoading}
              onClick={handleSendOtp}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg font-medium text-xs shadow-xs transition-colors disabled:opacity-40 mt-1"
            >
              {otpLoading ? 'Sending OTP via Firebase...' : 'Send OTP'}
            </button>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Enter 6-Digit OTP
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white/50 transition-colors tracking-widest text-center font-bold"
                  placeholder="• • • • • •"
                  required
                />
              </div>

              <button
                type="button"
                disabled={otpLoading}
                onClick={handleVerifyOtp}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg font-medium text-xs shadow-xs transition-colors disabled:opacity-40 mt-1"
              >
                {otpLoading ? 'Verifying...' : 'Verify & Login'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setOtpSent(false);
                  setOtp('');
                  setConfirmationResult(null);
                  if (window.recaptchaVerifier) {
                    try {
                      window.recaptchaVerifier.clear();
                    } catch (e) {}
                    window.recaptchaVerifier = null;
                  }
                }}
                className="w-full text-[11px] text-gray-500 hover:text-purple-600 text-center mt-1 transition-colors"
              >
                ← Change mobile number / Resend
              </button>
            </>
          )}
        </div>
      )}
      
      

      {/* Register Link */}
      <div className="mt-4 text-center">
        <Link
          to="/register"
          className="text-[11px] text-purple-600 hover:text-purple-700 font-medium transition-colors"
        >
          Don't have an account? Register
        </Link>
      </div>

      {/* Demo credentials */}
      <div className="mt-5 p-2.5 bg-gray-50 border border-gray-100 rounded-lg">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Demo Account</p>
        <div className="text-[11px] text-gray-600 flex justify-between">
          <span>guest@ponest.in</span>
          <span className="font-mono text-gray-400">Guest1</span>
        </div>
      </div>
    </div>
  );

  // If explicitly passed true, wrap it inside the modal screen overlay layout
  if (isOpen === true) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 antialiased animate-fade-in">
        {formContent}
        <style>{`
          .animate-fade-in { animation: fadeIn 0.2s ease-out; }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          .animate-fade-slide { animation: loginFadeSlide 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
          @keyframes loginFadeSlide { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        `}</style>
      </div>
    );
  }

  // Base routing layout view execution structure (for route path matches /login)
  return (
    <div className={isSidebar ? 'p-3' : 'min-h-screen bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 flex items-center justify-center p-4 antialiased'}>
      {formContent}
      <style>{`
        .animate-fade-slide { animation: loginFadeSlide 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
        @keyframes loginFadeSlide { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default Login;